import type { PropsWithChildren } from 'react';
import React from 'react';

import { Anchor, Paragraph } from '@zillow/constellation-10';

import { trackError } from '../utils/trackError';
import LeasesHeaderNav from './LeasesHeaderNav';
import { ZILLOW_LEASES_EMAIL } from '../constants/stringConstants';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { getErrorPageTextProps } from '../utils/getErrorPageTextProps';
import { ErrorPage } from './Widgets/ErrorPage';

type EnhancedError = Error & {
  userMessage?: string;
};

type State = {
  appIsBroken: boolean;
  error: EnhancedError | null;
};

/**
 * Component that renders an error if the app is an error state. Else, it renders its children
 */
export class ErrorCatcher extends React.Component<PropsWithChildren<{}>, State> {
  state: State = {
    appIsBroken: false,
    error: null,
  };

  componentDidCatch(error, info) {
    RpErrorBoundary.logError(error, info);
    trackError(error, info);

    this.setState({
      appIsBroken: true,
      error: error,
    });
  }

  render() {
    const { children } = this.props;
    const { appIsBroken, error } = this.state;

    const errorHeader = error?.message || 'Something went wrong';
    const errorMessage = error?.userMessage;

    return appIsBroken ? (
      <LeasesHeaderNav>
        <ErrorPage
          errorMessage={errorHeader}
          errorDetails={
            <>
              {errorMessage && <Paragraph css={{ marginBottom: 'loose' }}>{errorMessage}</Paragraph>}
              <Paragraph>
                For service, contact{' '}
                <Anchor
                  href={`mailto:${ZILLOW_LEASES_EMAIL}?subject=${encodeURIComponent(
                    'Problem with my lease',
                  )}&body=${encodeURIComponent(
                    `I tried going to "${window.location.pathname}", and encountered an error of "${
                      errorHeader.endsWith('.') ? errorHeader : `${errorHeader}.`
                    }"`,
                  )}`}
                >
                  {ZILLOW_LEASES_EMAIL}
                </Anchor>
              </Paragraph>
            </>
          }
          {...getErrorPageTextProps()}
        />
      </LeasesHeaderNav>
    ) : (
      children
    );
  }
}

export default ErrorCatcher;
